import React, { useState, useEffect, useRef } from 'react';
import {
  UsersIcon,
  UserGroupIcon,
  ClockIcon,
  GlobeAltIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import CounterAnimation from './CounterAnimation';

const WhyUsSection = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);

  const features = [
    {
      icon: UsersIcon,
      title: 'Comprometidos con el Éxito',
      description:
        'Con más de 300 colaboradores, trabajamos juntos para lograr un impacto significativo en cada proyecto que emprendemos.',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: ClockIcon,
      title: 'Más de 25 años de experiencia',
      description:
        'Nuestra longevidad es testimonio de nuestra capacidad para innovar y superar desafíos, siempre con un enfoque orientado al futuro.',
      color: 'from-orange-500 to-red-500',
    },
    {
      icon: GlobeAltIcon,
      title: 'Presencia Internacional',
      description:
        'Somos una empresa mexicana con presencia en Estados Unidos, Centroamérica y Sudamérica.',
      color: 'from-green-500 to-emerald-500',
    },
    {
      icon: BriefcaseIcon,
      title: 'Oportunidades de Crecimiento',
      description:
        'Promovemos el desarrollo profesional y personal con planes de carrera definidos.',
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: AcademicCapIcon,
      title: 'Formación Continua',
      description:
        'Invertimos en la capacitación y desarrollo de habilidades de nuestro equipo.',
      color: 'from-yellow-500 to-amber-500',
    },
    {
      icon: HeartIcon,
      title: 'Prestaciones Adicionales',
      description:
        'Tenemos convenios con proveedores de servicios que ofrecen beneficios a nuestros colaboradores.',
      color: 'from-red-500 to-rose-500',
    },
  ];

  // Detectar si es móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Funciones para el slider deslizante
  const goToSlide = (index) => {
    setCurrentSlide(index);
    setDragOffset(0);
  };

  // Touch/Mouse handlers para swipe/drag
  const handleDragStart = (e) => {
    setIsDragging(true);
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    setDragStartX(clientX);
    setTouchStart(clientX);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;

    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const diff = clientX - dragStartX;

    // Limitar el arrastre para que no sea infinito
    const maxDrag = 50;
    const limitedDiff = Math.max(Math.min(diff, maxDrag), -maxDrag);
    setDragOffset(limitedDiff);
    setTouchEnd(clientX);
  };

  const handleDragEnd = () => {
    if (isDragging) {
      setIsDragging(false);

      const distance = touchStart - touchEnd;
      const isLeftSwipe = distance > 50;
      const isRightSwipe = distance < -50;

      if (isLeftSwipe) {
        setCurrentSlide((prev) => (prev + 1) % features.length);
      } else if (isRightSwipe) {
        setCurrentSlide(
          (prev) => (prev - 1 + features.length) % features.length
        );
      }

      setDragOffset(0);
      setTouchStart(null);
      setTouchEnd(null);
    }
  };

  return (
    <section className="section-padding bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <span className="inline-block px-4 py-2 text-lg md:text-2xl bg-primary-blue/10 text-primary-blue rounded-full font-semibold mb-4">
            ¿POR QUÉ TRABAJAR EN PACKSYS?
          </span>
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Lideramos con <span className="text-primary-orange">talento</span> y{' '}
            <span className="text-primary-blue">compromiso</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600">
            Nos preocupamos por el bienestar de todos nuestros colaboradores,
            promoviendo el desarrollo profesional y personal. Estamos firmemente
            comprometidos con la formación continua y con el impulso de su
            crecimiento integral.
          </p>
        </div>

        {/* Video Section */}
        <div className="mb-12 md:mb-16">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black">
            <div
              className="relative w-full"
              style={{ paddingBottom: '56.25%' }}
            >
              <iframe
                src="https://www.youtube.com/embed/Fxud8RqAzWo?si=DlUDlkxwRlvPk2eq"
                title="Packsys Video"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>

        {/* Features - Versión Desktop (Grid) */}
        {!isMobile && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 card-hover border border-gray-100"
              >
                <div
                  className={`inline-flex p-4 rounded-xl bg-gradient-to-r ${feature.color} mb-6`}
                >
                  <feature.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 group-hover:text-primary-blue transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Features - Versión Móvil (Slider Deslizante) */}
        {isMobile && (
          <div className="relative py-6">
            {/* Slider Container */}
            <div
              ref={sliderRef}
              className="relative overflow-hidden px-2 cursor-grab active:cursor-grabbing select-none"
              onTouchStart={handleDragStart}
              onTouchMove={handleDragMove}
              onTouchEnd={handleDragEnd}
              onMouseDown={handleDragStart}
              onMouseMove={handleDragMove}
              onMouseUp={handleDragEnd}
              onMouseLeave={handleDragEnd}
            >
              <div
                className="flex transition-transform duration-300 ease-out"
                style={{
                  transform: `translateX(calc(-${currentSlide * 100}% + ${dragOffset}px))`,
                  transition: isDragging ? 'none' : 'transform 0.3s ease-out',
                }}
              >
                {features.map((feature, index) => (
                  <div key={index} className="w-full flex-shrink-0 px-4 py-4">
                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 min-h-[320px] flex flex-col">
                      <div
                        className={`inline-flex items-center justify-center p-4 rounded-xl bg-gradient-to-r ${feature.color} mb-4`}
                      >
                        <feature.icon className="h-7 w-7 text-white" />
                      </div>

                      <h3 className="text-xl text-center font-bold mb-3 text-gray-900">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 text-center leading-relaxed text-sm flex-grow">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Indicadores de slides (dots) - Solo visual, no clickeables */}
            <div className="flex justify-center gap-2 mt-6">
              {features.map((_, index) => (
                <span
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentSlide
                      ? 'w-6 bg-gradient-to-r from-orange-500 to-red-500'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Stats Section */}
        <div className="mt-16 md:mt-20 relative overflow-hidden rounded-3xl">
          {/* Fondo animado con gradiente naranja */}
          <div
            className="absolute inset-0 bg-gradient-to-r from-orange-600 via-amber-700 to-orange-800 animate-gradient-shift-stats"
            style={{ backgroundSize: '200% 200%' }}
          ></div>

          {/* Olas animadas - Ocultas en móvil */}
          <div className="hidden md:block absolute top-0 right-0 w-1/3 h-full bg-white/5 transform skew-x-12 animate-wave-right"></div>
          <div className="hidden md:block absolute bottom-0 left-0 w-1/3 h-full bg-white/5 transform -skew-x-12 animate-wave-left"></div>

          {/* Brillo animado */}
          <div className="absolute inset-0 animate-shimmer-effect">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12"></div>
          </div>

          <div className="relative z-10 rounded-3xl p-8 md:p-16 text-white">
            <div className="text-center mb-10 md:mb-12">
              <h3 className="text-2xl md:text-5xl font-bold mb-3 md:mb-4 text-white">
                Nuestra trayectoria nos respalda
              </h3>
              <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto px-4">
                Más de dos décadas de crecimiento, innovación y compromiso con
                la excelencia
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {/* Colaboradores */}
              <div className="group">
                <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/20 hover:border-white/40 transition-all duration-500 hover:scale-105 hover:bg-white/15">
                  <div className="text-center">
                    <div className="relative inline-block mb-3 md:mb-4">
                      <div className="text-5xl md:text-7xl font-bold bg-gradient-to-b from-white to-white/80 bg-clip-text text-transparent">
                        300+
                      </div>
                    </div>

                    <div className="space-y-2 md:space-y-3">
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-white/20 to-white/5 rounded-full flex items-center justify-center">
                          <UserGroupIcon className="w-4 h-4 md:w-5 md:h-5" />
                        </div>
                        <h4 className="text-base md:text-lg font-bold text-white">
                          Colaboradores
                        </h4>
                      </div>
                      <p className="text-white/70 text-xs md:text-sm leading-relaxed px-2">
                        Talentos que forman nuestra gran familia
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Años de experiencia */}
              <div className="group">
                <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/20 hover:border-white/40 transition-all duration-500 hover:scale-105 hover:bg-white/15">
                  <div className="text-center">
                    <div className="relative inline-block mb-3 md:mb-4">
                      <div className="text-5xl md:text-7xl font-bold bg-gradient-to-b from-white to-white/80 bg-clip-text text-transparent">
                        25+
                      </div>
                    </div>

                    <div className="space-y-2 md:space-y-3">
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-white/20 to-white/5 rounded-full flex items-center justify-center">
                          <svg
                            className="w-4 h-4 md:w-5 md:h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <h4 className="text-base md:text-lg font-bold text-white">
                          Años de experiencia
                        </h4>
                      </div>
                      <p className="text-white/70 text-xs md:text-sm leading-relaxed px-2">
                        Liderando la industria con innovación y calidad
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Países */}
              <div className="group">
                <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/20 hover:border-white/40 transition-all duration-500 hover:scale-105 hover:bg-white/15">
                  <div className="text-center">
                    <div className="relative inline-block mb-3 md:mb-4">
                      <div className="text-5xl md:text-7xl font-bold bg-gradient-to-b from-white to-white/80 bg-clip-text text-transparent">
                        6
                      </div>
                    </div>

                    <div className="space-y-2 md:space-y-3">
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-white/20 to-white/5 rounded-full flex items-center justify-center">
                          <svg
                            className="w-4 h-4 md:w-5 md:h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <h4 className="text-base md:text-lg font-bold text-white">
                          Países
                        </h4>
                      </div>
                      <p className="text-white/70 text-xs md:text-sm leading-relaxed px-2">
                        Presencia internacional con impacto global
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes gradientShift {
          0%,
          100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        @keyframes waveRight {
          0%,
          100% {
            transform: skewX(12deg) translateX(0);
          }
          50% {
            transform: skewX(12deg) translateX(20px);
          }
        }
        @keyframes waveLeft {
          0%,
          100% {
            transform: skewX(-12deg) translateX(0);
          }
          50% {
            transform: skewX(-12deg) translateX(-20px);
          }
        }
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        .animate-gradient-shift-stats {
          animation: gradientShift 8s ease infinite;
        }
        .animate-wave-right {
          animation: waveRight 6s ease-in-out infinite;
        }
        .animate-wave-left {
          animation: waveLeft 7s ease-in-out infinite;
        }
        .animate-shimmer-effect {
          animation: shimmer 3s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default WhyUsSection;
